import React from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import QuickFact, {
  QuickFactHighlight,
} from '@rover/react-lib/src/pages/landing/components/QuickFact';
import ServiceBenefits, {
  ServiceBenefitsImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/ServiceBenefits';
import ServiceLandingServicesWrapper from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServicesWrapper';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import {
  ROVER_DATA_CITATION,
  Superscript,
} from '@rover/react-lib/src/pages/shared/FooterLinks/FactCitations';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import {
  SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM,
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import {
  dogAndCat as petSittingServiceBenefitsMobileImage,
  dogAndCatCloud as petSittingServiceBenefitsImage,
} from '../../components/images';
import servicesLinksImage from '../../components/images/cat-looking-upward.png';
import RoverProtectCondensed from '../../components/RoverProtectCondensed';
import PetSittingServiceFaqs from '../../components/ServiceFaqs/PetSittingServiceFaqs';
import ServiceLandingSection, {
  SERVICE_LANDING_SECTION_MARGIN_Y,
} from '../../components/ServiceLandingSection';
import { SERVICE_SEARCH_FORM_SUBMIT_TYPES } from '../../constants';
import { quickFactStickerChihuahua, quickFactStickerPug } from '../shared-images';

import dogPetSittingHeroDesktopImage from './images/pet-sitting-hero-desktop.jpg';
import dogPetSittingHeroTabletImage from './images/pet-sitting-hero-tablet.jpg';

const petSittingImages: HeroImageSrcSet = {
  desktop: {
    src: dogPetSittingHeroDesktopImage,
    height: 500,
  },
  tablet: {
    src: dogPetSittingHeroTabletImage,
    height: 500,
  },
};

const petSittingServiceBenefitsImageSrcSet: ServiceBenefitsImageSrcSet = {
  desktop: {
    src: petSittingServiceBenefitsImage,
    width: 589,
    height: 493,
  },
  mobile: {
    src: petSittingServiceBenefitsMobileImage,
    width: 393,
    height: 186,
  },
};

const PetSitting: React.FC<Props> = ({
  serviceLinks,
  countryConfig,
  locale,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  serviceTypes,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const { t, i18n } = useLingui();
  const serviceLandingType = ServiceLandingEnum.PET_SITTING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
  // Tags analytics events with the real booking service type rather than the SEO-slug vocabulary
  // serviceTypeSlug above uses (e.g. 'overnight-boarding', not 'dog-boarding').
  const internalServiceTypeSlug = SERVICE_TYPE_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;
  const { localizedLinkUrls } = camelize(countryConfig.urls, true);

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet
        title={seoTitle}
        metaDescription={seoMetaDescription}
        indexable={serviceLandingData.indexable}
      />
      <LandingHero mb="0x" heroImageSrcSet={petSittingImages} upliftedContent>
        <LandingHero.Content upliftedContent>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            locale={locale}
            serviceTypes={serviceTypes}
            internalServiceType={{ slug: internalServiceTypeSlug }}
            initialSearchFilters={initialSearchFilters}
            showSearchFilters={false}
            submitButtonTextVariant={SERVICE_SEARCH_FORM_SUBMIT_TYPES.PET_SITTERS}
          />
        </LandingHero.Content>
      </LandingHero>

      {/* Includes Section within component */}
      <RoverProtectCondensed countryConfig={countryConfig} mb={SERVICE_LANDING_SECTION_MARGIN_Y} />

      {/* Includes Section & margins within component */}
      <ServiceBenefits
        serviceBenefitsImageSrcSet={petSittingServiceBenefitsImageSrcSet}
        title={<Trans>Sitters who’ll check in on your pets at home</Trans>}
      >
        <ServiceBenefits.List>
          <ServiceBenefits.ListItem title={<Trans>Pet sitters you can trust</Trans>}>
            <Trans>
              We make it easy to find 5-star, background-checked sitters. Check out sitter profiles
              and read verified reviews to find a sitter your dog will look forward to staying with.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Personalized care on your schedule</Trans>}>
            <Trans>
              Book 30 or 60-minute visits and set them to reoccur weekly. Your pet’s care stays
              on-track and carefree.
            </Trans>
          </ServiceBenefits.ListItem>
          <ServiceBenefits.ListItem title={<Trans>Get photos & updates</Trans>}>
            <Trans>
              Find out how your dog is doing by requesting updates in the Rover app. Get photos from
              your sitter of everything from playtime to nap time.
            </Trans>
          </ServiceBenefits.ListItem>
        </ServiceBenefits.List>
      </ServiceBenefits>

      {searchResults && searchResults.results?.length > 0 && (
        <ServiceLandingSection>
          <LandingSearchResultsCarousel
            searchResponse={searchResults}
            searchFilters={initialSearchFilters}
            locale={locale}
            heading={t`Highly-rated pet sitters in your area`}
            headingVariant="display"
            serviceTypeSlug={serviceTypeSlug}
            countryConfig={countryConfig}
          />
        </ServiceLandingSection>
      )}

      {/* Includes Section within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="yellow"
        stickerImageSrc={quickFactStickerChihuahua}
      >
        <Trans>
          More than <QuickFactHighlight>1.6 million</QuickFactHighlight> pups enjoyed cuddles and
          care from sitters on Rover
        </Trans>
        <Superscript>1</Superscript>
      </QuickFact>

      <ServiceLandingSection>
        <PetSittingServiceFaqs
          localizedLinkUrls={localizedLinkUrls}
          countryConfig={countryConfig}
          serviceLinks={serviceLinks}
        />
      </ServiceLandingSection>

      {/* Includes Section within component */}
      <QuickFact
        my={SERVICE_LANDING_SECTION_MARGIN_Y}
        themeColor="pink"
        stickerImageSrc={quickFactStickerPug}
      >
        <Trans>
          <QuickFactHighlight>15 to 45 minute</QuickFactHighlight> interactions with people may help
          dogs relax more
        </Trans>
        <Superscript>2</Superscript>
      </QuickFact>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>How to book drop-in visits</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Browse profiles of nearby sitters who offer drop-in visits.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Meet in person</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Schedule a free in-person Meet & Greet to find the perfect fit.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Book &amp; pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                It's that easy! All payments are processed through Rover's secured platform.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      {/* Includes Section & margins within component */}
      <ServiceLandingServicesWrapper
        serviceLandingType={serviceLandingType}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
      </ServiceLandingServicesWrapper>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>

      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
        citations={[
          i18n._(ROVER_DATA_CITATION),
          <>
            Gunter, L. M., et al. (2021).{' '}
            <cite>
              Investigating the impact of brief outings on the welfare of dogs living in US
              shelters.
            </cite>{' '}
            Animals, 11 (2), 548.
          </>,
        ]}
      />
    </>
  );
};

export default PetSitting;
